import { Link } from 'react-router-dom';
import ContactActions from '../components/ContactActions';
import CtaBand from '../components/CtaBand';
import FaqList from '../components/FaqList';
import GuideCard from '../components/GuideCard';
import HeroArt from '../components/HeroArt';
import Icon from '../components/Icon';
import Layout from '../components/Layout';
import Ornament from '../components/Ornament';
import { HERO_POINTS, HOME_FAQS, SERVICES, STATS, STEPS, TROUBLE_AREAS, UPDATE_AREAS, WHY_US } from '../constants/content';
import { GUIDES, guidePath } from '../constants/guides';

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];

const TROUBLE_INDEX = [
  ['I cannot sign in to my account', 'Account', 'cant-sign-in-to-ancestry'],
  ['The verification code never arrives', 'Account', 'two-step-verification-help'],
  ['The same ancestor appears twice', 'Family Tree', 'fix-duplicate-people-in-tree'],
  ['A parent or spouse is linked wrongly', 'Family Tree', 'correct-wrong-relationships'],
  ['My DNA kit code will not activate', 'DNA', 'activate-ancestry-dna-kit'],
  ['My ethnicity estimate has changed', 'DNA', 'dna-ethnicity-estimate-updates'],
  ['The app crashes after an update', 'Updates', 'mobile-app-not-working'],
  ['Desktop software will not sync', 'Updates', 'sync-desktop-tree-software'],
];

const FEATURED = ['cant-sign-in-to-ancestry', 'understanding-dna-matches', 'get-better-search-results'].map((slug) =>
  GUIDES.find((g) => g.slug === slug)
);

export default function HomePage() {
  return (
    <Layout>
      <section className="hero">
        <div className="container hero__frame">
          <div className="hero__grid">
            <div className="hero__copy">
              <p className="eyebrow">Independent · Patient · Plain-spoken</p>
              <h1>
                Ancestry Helps, Updates <em>&amp;</em> Troubleshooting
              </h1>
              <p className="hero__sub">Keeping your family story in good order since your very first record.</p>
              <p className="hero__lead">
                When sign-in fails, a tree goes astray, a DNA kit will not activate, or an update moves everything
                around — our technicians help you put it right, step by step, at any hour.
              </p>
              <ContactActions chatLabel="Speak with a Technician" />
              <ul className="hero__ticks">
                {HERO_POINTS.map((point) => (
                  <li key={point}>
                    <Icon name="check" size={16} /> {point}
                  </li>
                ))}
              </ul>
            </div>
            <div className="hero__art">
              <HeroArt />
            </div>
          </div>
        </div>
      </section>

      <section className="ledger-strip">
        <div className="container">
          <ul className="stat-row">
            {STATS.map((s) => (
              <li key={s.label}>
                <strong>{s.value}</strong>
                <span>{s.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section">
        <div className="container container--mid">
          <div className="section-head">
            <p className="eyebrow">The Troubleshooting Index</p>
            <h2>Find your trouble, turn to the remedy</h2>
            <Ornament />
            <p>The problems we are asked about most, each with a step-by-step fix. Can’t see yours? Try the full finder.</p>
          </div>
          <table className="index-table">
            <thead>
              <tr>
                <th scope="col">No.</th>
                <th scope="col">The Trouble</th>
                <th scope="col">Section</th>
                <th scope="col">
                  <span className="sr-only">Remedy</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {TROUBLE_INDEX.map(([label, section, slug], i) => (
                <tr key={slug}>
                  <td className="index-table__no">{ROMAN[i]}.</td>
                  <td className="index-table__title">
                    <Link to={guidePath(slug)}>{label}</Link>
                  </td>
                  <td className="index-table__sec">{section}</td>
                  <td className="index-table__go">
                    <Link to={guidePath(slug)} aria-label={`Read the remedy: ${label}`}>
                      Remedy →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="actions actions--center">
            <Link to="/troubleshooting" className="btn btn--dark">
              Open the full troubleshooting finder
            </Link>
          </div>
        </div>
      </section>

      <section className="section section--paper">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Chapters of Service</p>
            <h2>Help for every part of your Ancestry account</h2>
            <Ornament />
            <p>From the first sign-in to a ten-generation tree, we have seen it — and set it right — before.</p>
          </div>
          <div className="chapter-grid">
            {SERVICES.map((s, i) => (
              <article key={s.title} className="chapter">
                <span className="chapter__num">Chapter {ROMAN[i]}</span>
                <Icon name={s.icon} size={30} />
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </article>
            ))}
          </div>
          <div className="actions actions--center">
            <Link to="/helps-services" className="btn btn--outline">
              Read all helps services
            </Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="gazette">
            <header className="gazette__head">
              <span>Vol. I · Updates Edition</span>
              <h2>The Updates Gazette</h2>
              <span>What changes, and what to check afterwards</span>
            </header>
            <div className="gazette__cols">
              {UPDATE_AREAS.map((u) => (
                <article key={u.title}>
                  <h3>{u.title}</h3>
                  <p>{u.text}</p>
                </article>
              ))}
            </div>
            <footer className="gazette__foot">
              <span>Continued in the Updates Hub —</span>
              <Link to="/updates" className="btn btn--primary">
                <Icon name="bell" size={16} /> Read the full updates page
              </Link>
            </footer>
          </div>
        </div>
      </section>

      <section className="section section--green">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow eyebrow--light">Our Method</p>
            <h2>Three plain steps back to your research</h2>
            <Ornament light />
          </div>
          <ol className="step-grid">
            {STEPS.map((step, i) => (
              <li key={step.title} className="step-card">
                <span className="step-card__num">{ROMAN[i]}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
          <ul className="area-pills">
            {TROUBLE_AREAS.map((area) => (
              <li key={area.key}>
                <Link to={`/troubleshooting?area=${area.key}`}>
                  <Icon name={area.icon} size={18} /> {area.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Our Promise</p>
            <h2>Help that respects your time and your records</h2>
            <Ornament />
          </div>
          <div className="promise-grid">
            {WHY_US.map((w) => (
              <article key={w.title} className="why-card">
                <Icon name={w.icon} size={28} />
                <h3>{w.title}</h3>
                <p>{w.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--paper">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">From the Guide Library</p>
            <h2>Most-read help articles</h2>
            <Ornament />
          </div>
          <div className="card-grid card-grid--3">
            {FEATURED.map((g) => (
              <GuideCard key={g.slug} guide={g} />
            ))}
          </div>
          <div className="actions actions--center">
            <Link to="/guides" className="btn btn--outline">
              Browse the whole library
            </Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container container--narrow">
          <div className="section-head">
            <p className="eyebrow">Questions &amp; Answers</p>
            <h2>Good to know</h2>
            <Ornament />
          </div>
          <FaqList items={HOME_FAQS} />
          <div className="actions actions--center">
            <Link to="/faqs" className="btn btn--outline">
              More questions answered
            </Link>
          </div>
        </div>
      </section>

      <CtaBand />
    </Layout>
  );
}
